import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Alert,
  Snackbar,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import JoditEditor from 'jodit-react';
import communicationService from '../services/communicationService';
import { useActionLock } from '../hooks/useActionLock';
import ActionButton from '../components/ActionButton';

const Communications = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState('email'); // 'email' | 'sms'
  const [editorTab, setEditorTab] = useState('visual'); // 'visual' | 'preview'
  const [templateWrapper, setTemplateWrapper] = useState({ prefix: '', suffix: '' });
  const [showPasteHtmlDialog, setShowPasteHtmlDialog] = useState(false);
  const [pastedHtml, setPastedHtml] = useState('');
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Dropdown data
  const [purposes, setPurposes] = useState([]);
  const [variables, setVariables] = useState([]);
  const [variableMappingAddValue, setVariableMappingAddValue] = useState('');

  // Saving state (action lock prevents double submit)
  const [saving, runSave] = useActionLock();
  const [deleting, runDelete] = useActionLock();

  const [filters, setFilters] = useState({
    search: '',
  });

  // Pagination state
  const [page, setPage] = useState(0); // 0-indexed for MUI TablePagination
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingComm, setEditingComm] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commToDelete, setCommToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    purpose: '',
    template_name: '',
    subject: '',
    email_content: '',
    sms_content: '',
    variable_mapping: '',
    trigger_condition: '',
    frequency: '',
    recipient_type: '',
    // For SMS add only
    template_id: '',
    dlt_content_template_id: '',
  });

  const iframeRef = useRef(null);

  const handleIframeLoad = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      try {
        const height = iframeRef.current.contentWindow.document.documentElement.scrollHeight;
        iframeRef.current.style.height = `${height + 20}px`;
      } catch (e) {
        console.error("Error resizing iframe:", e);
      }
    }
  };

  useEffect(() => {
    if (editorTab === 'preview') {
      const timer = setTimeout(handleIframeLoad, 100);
      return () => clearTimeout(timer);
    }
  }, [formData.email_content, editorTab]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const stripHtml = (html = '') => {
    if (!html) return '';
    const text = html.replace(/<[^>]+>/g, ' ');
    return text.replace(/\s+/g, ' ').trim();
  };

  const shortenText = (text = '', maxLength = 220) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength)}...`;
  };

  const fetchCommunications = useCallback(
    async (channel, currentPage = page, currentPageSize = pageSize) => {
      setLoading(true);
      setError(null);

      const type = channel || activeTab;

      const params = {
        page: currentPage + 1, // API uses 1-indexed pages
        page_size: currentPageSize,
      };

      // search filter applied on backend (name, subject, content)
      if (filters.search?.trim()) {
        params.search = filters.search.trim();
      }

      const result =
        type === 'email'
          ? await communicationService.getEmailTemplates(params)
          : await communicationService.getSmsTemplates(params);

      if (result.success) {
        const rawList = Array.isArray(result.data) ? result.data : result.data.results || [];

        const mapped = (rawList || []).map((template) => {
          if (type === 'email') {
            return {
              id: template.template_id || template.id,
              type: 'Email',
              template_name: template.template_name || '',
              purpose: template.purpose || '',
              frequency: template.frequency || '',
              recipient_type: template.recipient_type || '',
              subject: template.subject || '',
              content: shortenText(
                stripHtml(
                  template.email_content || template.content || template.template_content || ''
                )
              ),
              raw: template,
            };
          }

          // SMS mapping
          return {
            id: template.template_id || template.id,
            type: 'SMS',
            template_name: template.template_name || '',
            purpose: template.purpose || '',
            frequency: template.frequency || '',
            recipient_type: template.recipient_type || '',
            subject: template.subject || '',
            content: shortenText(
              template.template_content || template.sms_content || template.content || ''
            ),
            raw: template,
          };
        });

        setCommunications(mapped);
        setTotalCount(result.count || 0);
      } else {
        setError(result.error);
        setCommunications([]);
        setTotalCount(0);
      }

      setLoading(false);
    },
    [activeTab, filters, page, pageSize]
  );

  useEffect(() => {
    fetchCommunications(activeTab, page, pageSize);
  }, [activeTab, fetchCommunications, page, pageSize]);

  // Fetch purposes and variables on mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      const [purposesResult, variablesResult] = await Promise.all([
        communicationService.getPurposes(),
        communicationService.getVariables(),
      ]);
      if (purposesResult.success) {
        setPurposes(purposesResult.data);
      }
      if (variablesResult.success) {
        setVariables(variablesResult.data);
      }
    };
    fetchDropdownData();
  }, []);

  const handleTabChange = (event, newValue) => {
    setPage(0); // Reset to first page on tab change
    setActiveTab(newValue);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when page size changes
  };

  const handleSearchChange = (event) => {
    setFilters((prev) => ({ ...prev, search: event.target.value }));
  };

  const handleApplyFilters = () => {
    setPage(0); // Reset to first page when applying filters
    fetchCommunications(activeTab, 0, pageSize);
  };

  const handleAddNew = () => {
    setFormData({
      purpose: '',
      template_name: '',
      subject: '',
      email_content: '',
      sms_content: '',
      variable_mapping: [],
      trigger_condition: '',
      frequency: '',
      recipient_type: '',
      template_id: '',
      dlt_content_template_id: '',
    });
    setTemplateWrapper({ prefix: '', suffix: '' });
    setEditingComm(null);
    setEditorTab('visual');
    setVariableMappingAddValue('');
    setShowAddForm(true);
  };

  const handleEdit = (comm) => {
    // Use raw template data (full HTML/text) for the dialog
    const template = comm.raw || comm;
    setVariableMappingAddValue('');

    setFormData({
      purpose: template.purpose || '',
      template_name: template.template_name || '',
      subject: template.subject || '',
      email_content: template.email_content || '',
      sms_content: template.template_content || template.sms_content || template.content || '',
      variable_mapping: typeof template.variable_mapping === 'string'
        ? template.variable_mapping.split(',').map(s => s.trim()).filter(Boolean)
        : (Array.isArray(template.variable_mapping) ? template.variable_mapping : []),
      trigger_condition: template.trigger_condition || '',
      frequency: template.frequency || '',
      recipient_type: template.recipient_type || '',
      template_id: template.dlt_content_template_id || '',
      dlt_content_template_id: template.template_id || '',
    });

    let emailContent = template.email_content || '';
    let prefix = '';
    let suffix = '';

    if (activeTab === 'email' && emailContent.includes('<body')) {
      const bodyStartMatch = emailContent.match(/<body[^>]*>/i);
      const bodyEndMatch = emailContent.match(/<\/body>/i);
      if (bodyStartMatch && bodyEndMatch) {
        const startIdx = bodyStartMatch.index + bodyStartMatch[0].length;
        const endIdx = bodyEndMatch.index;
        prefix = emailContent.substring(0, startIdx);
        suffix = emailContent.substring(endIdx);
        emailContent = emailContent.substring(startIdx, endIdx);

        // Update formData with extracted content
        setFormData(prev => ({ ...prev, email_content: emailContent }));
      }
    }

    setTemplateWrapper({ prefix, suffix });
    setEditingComm(comm);
    setEditorTab('visual');
    setShowAddForm(true);
  };

  const handleImportHtml = () => {
    if (!pastedHtml.trim()) return;

    // Unescape literal \n and \" if they exist (common when copying from stringified sources)
    let emailContent = pastedHtml
      .replace(/\\n/g, '\n')
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      .replace(/\\t/g, '\t');

    let prefix = '';
    let suffix = '';

    // Robust body extraction
    const bodyStartMatch = emailContent.match(/<body[^>]*>/i);
    const bodyEndMatch = emailContent.match(/<\/body>/i);

    if (bodyStartMatch && bodyEndMatch) {
      const startIdx = bodyStartMatch.index + bodyStartMatch[0].length;
      const endIdx = bodyEndMatch.index;
      prefix = emailContent.substring(0, startIdx);
      suffix = emailContent.substring(endIdx);
      emailContent = emailContent.substring(startIdx, endIdx);
    } else {
      // If no body tags, assume the whole thing is content and clear wrapper
      prefix = '';
      suffix = '';
    }

    setTemplateWrapper({ prefix, suffix });
    setFormData(prev => ({ ...prev, email_content: emailContent }));
    setShowPasteHtmlDialog(false);
    setPastedHtml('');
    setEditorTab('visual');
    showSnackbar('HTML Template imported successfully');
  };

  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // Variable mapping: allow same variable multiple times (append-only add, remove by index)
  const handleAddVariableMapping = (key) => {
    const current = Array.isArray(formData.variable_mapping) ? formData.variable_mapping : [];
    handleFormChange('variable_mapping', [...current, key]);
  };
  const handleRemoveVariableMapping = (index) => {
    const current = Array.isArray(formData.variable_mapping) ? formData.variable_mapping : [];
    handleFormChange('variable_mapping', current.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      let result;

      if (activeTab === 'email') {
        const emailPayload = {
          template_name: formData.template_name,
          subject: formData.subject,
          email_content: templateWrapper.prefix + formData.email_content + templateWrapper.suffix,
          variable_mapping: Array.isArray(formData.variable_mapping) ? formData.variable_mapping : [],
          trigger_condition: formData.trigger_condition,
          frequency: 'frequent',
          recipient_type: formData.recipient_type,
        };

        if (editingComm) {
          // Get purpose from editing comm
          const purpose = editingComm.raw?.purpose || formData.purpose;
          result = await communicationService.updateEmailTemplate(purpose, emailPayload);
        } else {
          // Add purpose for create
          emailPayload.purpose = formData.purpose;
          result = await communicationService.createEmailTemplate(emailPayload);
        }
      } else {
        // SMS
        if (editingComm) {
          const smsEditPayload = {
            template_id: formData.template_id,
            template_name: formData.template_name,
            variable_mapping: Array.isArray(formData.variable_mapping) ? formData.variable_mapping : [],
            template_content: formData.sms_content || 'fake content',
            trigger_condition: 'condition',
            frequency: 'frequent',
            sender_id: 'MRZMDR',
          };
          const purpose = editingComm.raw?.purpose || formData.purpose;
          result = await communicationService.updateSmsTemplate(purpose, smsEditPayload);
        } else {
          const smsAddPayload = {
            template_id: formData.dlt_content_template_id,
            purpose: formData.purpose,
            template_name: formData.template_name,
            dlt_content_template_id: formData.template_id || Date.now(),
            variable_mapping: Array.isArray(formData.variable_mapping) ? formData.variable_mapping : [],
            template_content: formData.sms_content || 'fake content',
            trigger_condition: 'condition',
            frequency: 'frequent',
            sender_id: 'MRZMDR',
          };
          result = await communicationService.createSmsTemplate(smsAddPayload);
        }
      }

      if (result.success) {
        showSnackbar(editingComm ? 'Template updated successfully' : 'Template created successfully');
        setShowAddForm(false);
        // Refresh the list
        fetchCommunications(activeTab, page, pageSize);
      } else {
        showSnackbar(result.error || 'Failed to save template', 'error');
      }
    } catch (err) {
      showSnackbar('An error occurred while saving.', 'error');
    }
  };

  const handleDelete = (comm) => {
    setCommToDelete(comm);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!commToDelete) return;

    try {
      const purpose = commToDelete.raw?.purpose || commToDelete.purpose;
      let result;

      if (activeTab === 'email') {
        result = await communicationService.deleteEmailTemplate(purpose);
      } else {
        result = await communicationService.deleteSmsTemplate(purpose);
      }

      if (result.success) {
        showSnackbar('Template deleted successfully');
        setDeleteDialogOpen(false);
        setCommToDelete(null);
        // Refresh the list
        fetchCommunications(activeTab, page, pageSize);
      } else {
        showSnackbar(result.error || 'Failed to delete template', 'error');
      }
    } catch (err) {
      console.error("Error deleting template:", err);
      showSnackbar("Failed to delete template", "error");
    }
  };

  return (
    <Box sx={{ padding: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          padding: { xs: 1.5, sm: 2, md: 2.5 },
          marginBottom: { xs: 2, md: 3 },
          borderRadius: 2,
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
          border: '1px solid #ECF0F1',
        }}
      >
        {/* Header Row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, fontSize: { xs: '18px', sm: '20px' }, color: 'white' }}
          >
            Manage Communications
          </Typography>
        </Box>
      </Paper>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid #4E83BD',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 0 },
            padding: 2,
            borderBottom: '1px solid #4E83BD',
            backgroundColor: '#F9FAFB',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            textColor="inherit"
            variant={isMobile ? 'fullWidth' : 'standard'}
            sx={{
              minHeight: 0,
              '& .MuiTab-root': {
                color: '#4E83BD',
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 0,
                paddingX: 0,
                paddingY: 1,
              },
              '& .Mui-selected': {
                color: '#4E83BD',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#4E83BD',
              },
            }}
          >
            <Tab label="Email" value="email" />
            <Tab label="SMS" value="sms" />
          </Tabs>

          <Button
            variant="contained"
            startIcon={!isMobile && <Add />}
            onClick={handleAddNew}
            sx={{
              textTransform: 'none',
              backgroundColor: '#4E83BD',
              color: 'white',
              fontSize: { xs: '12px', sm: '13px' },
              padding: { xs: '6px 12px', sm: '8px 16px' },
              borderRadius: '6px',
              backdropFilter: 'blur(10px)',
              fontWeight: 600,
              border: '1px solid #4E83BD',
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                backgroundColor: '#4E83BD',
              },
            }}
          >
            {isMobile ? <Add sx={{ mr: 0.5 }} /> : null}
            {activeTab === 'email' ? 'Add Email' : 'Add SMS'}
          </Button>
        </Box>
        {/* Filters Row */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 2 },
            padding: { xs: 1.5, sm: 2 },
            alignItems: { xs: 'flex-start', sm: 'center' },
            borderBottom: '1px solid #ECF0F1',
            backgroundColor: '#F9FAFB',
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
          }}
        >
          <TextField
            size="small"
            placeholder="Search by name, subject, content....."
            value={filters.search}
            onChange={handleSearchChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleApplyFilters();
              }
            }}
            sx={{
              border: '1px solid #4E83BD',
              borderRadius: '8px',
              backgroundColor: 'white',
              '& .MuiInputBase-input': {
                fontSize: { xs: '13px', sm: '14px' },
              },
              width: { xs: '100%', sm: '20%' },
            }}
          />
        </Box>

        {/* Mobile Card View */}
        {isMobile ? (
          <Box sx={{ padding: { xs: 1.5, sm: 2 } }}>
            {loading ? (
              <Box sx={{ textAlign: 'center', padding: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : error ? (
              <Box sx={{ textAlign: 'center', padding: 4, color: '#DC2626' }}>
                {error}
              </Box>
            ) : communications.length === 0 ? (
              <Box sx={{ textAlign: 'center', padding: 4, color: '#6B7280' }}>
                No communications found.
              </Box>
            ) : (
              communications.map((comm, index) => (
                <Box
                  key={comm.id}
                  sx={{
                    backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F8FAFC',
                    borderRadius: '8px',
                    padding: 2,
                    marginBottom: 1.5,
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  }}
                >
                  {/* Card Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip
                          label={comm.type}
                          size="small"
                          sx={{
                            backgroundColor: '#4E83BD',
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: 600,
                            height: '22px',
                          }}
                        />
                      </Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          color: '#2C3E50',
                          fontSize: '15px',
                        }}
                      >
                        {comm.template_name || comm.subject}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(comm)}
                        sx={{
                          color: '#4E83BD',
                          backgroundColor: 'rgba(78, 131, 189, 0.1)',
                          padding: '6px',
                        }}
                      >
                        <Edit sx={{ fontSize: '18px' }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(comm)}
                        sx={{
                          color: '#DC2626',
                          backgroundColor: 'rgba(220, 38, 38, 0.1)',
                          padding: '6px',
                        }}
                      >
                        <Delete sx={{ fontSize: '18px' }} />
                      </IconButton>
                    </Box>
                  </Box>
                  {/* Card Content */}
                  <Box sx={{ mt: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 500 }}>Purpose:</Typography>
                      <Typography sx={{ fontSize: '12px', color: '#1F2937', fontWeight: 600 }}>
                        {comm.purpose ? comm.purpose.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-'}
                      </Typography>
                    </Box>
                    {activeTab === 'email' && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 500 }}>Recipient:</Typography>
                        <Typography sx={{ fontSize: '12px', color: '#1F2937' }}>{comm.recipient_type || '-'}</Typography>
                      </Box>
                    )}
                    {activeTab === 'sms' && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 500 }}>Template ID:</Typography>
                        <Typography sx={{ fontSize: '12px', color: '#1F2937', fontWeight: 600 }}>{comm.id}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        ) : (
          /* Desktop/Tablet Table View */
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: isTablet ? 600 : 'auto' }}>
              <TableHead>
                <TableRow
                  sx={{
                    background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
                  }}
                >
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: { sm: '13px', md: '14px' },
                      padding: { sm: '12px', md: '16px' },
                      width: '40%',
                    }}
                  >
                    Template Name
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: { sm: '13px', md: '14px' },
                      padding: { sm: '12px', md: '16px' },
                      width: '25%',
                    }}
                  >
                    Purpose
                  </TableCell>
                  {activeTab === 'email' && (
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: { sm: '13px', md: '14px' },
                        padding: { sm: '12px', md: '16px' },
                        width: '20%',
                      }}
                    >
                      Recipient Type
                    </TableCell>
                  )}
                  {activeTab === 'sms' && (
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: { sm: '13px', md: '14px' },
                        padding: { sm: '12px', md: '16px' },
                        width: '15%',
                      }}
                    >
                      Template ID
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: { sm: '13px', md: '14px' },
                      padding: { sm: '12px', md: '16px' },
                      width: '15%',
                      textAlign: 'center',
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={activeTab === 'email' ? 5 : 4} sx={{ textAlign: 'center', padding: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={activeTab === 'email' ? 5 : 4} sx={{ textAlign: 'center', padding: 4, color: '#DC2626' }}>
                      {error}
                    </TableCell>
                  </TableRow>
                ) : communications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={activeTab === 'email' ? 5 : 4} sx={{ textAlign: 'center', padding: 4 }}>
                      No communications found.
                    </TableCell>
                  </TableRow>
                ) : (
                  communications.map((comm, index) => (
                    <TableRow
                      key={comm.id}
                      sx={{
                        backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F8FAFC',
                        '&:hover': {
                          backgroundColor: '#ECF0F1',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          color: '#1F2937',
                          fontSize: { sm: '13px', md: '14px' },
                          padding: { sm: '12px', md: '16px' },
                          fontWeight: 600,
                        }}
                      >
                        {comm.template_name || comm.subject}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: '#4B5563',
                          fontSize: { sm: '13px', md: '14px' },
                          padding: { sm: '12px', md: '16px' },
                        }}
                      >
                        {comm.purpose ? comm.purpose.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-'}
                      </TableCell>
                      {activeTab === 'email' && (
                        <TableCell
                          sx={{
                            color: '#4B5563',
                            fontSize: { sm: '13px', md: '14px' },
                            padding: { sm: '12px', md: '16px' },
                          }}
                        >
                          {comm.recipient_type}
                        </TableCell>
                      )}
                      {activeTab === 'sms' && (
                        <TableCell
                          sx={{
                            color: '#4B5563',
                            fontSize: { sm: '13px', md: '14px' },
                            padding: { sm: '12px', md: '16px' },
                          }}
                        >
                          {comm.id}
                        </TableCell>
                      )}
                      <TableCell sx={{ padding: { sm: '12px', md: '16px' }, textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(comm)}
                            sx={{
                              color: '#4E83BD',
                              backgroundColor: 'rgba(78, 131, 189, 0.1)',
                              '&:hover': {
                                backgroundColor: 'rgba(78, 131, 189, 0.2)',
                              },
                            }}
                          >
                            <Edit sx={{ fontSize: { sm: '18px', md: '20px' } }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(comm)}
                            sx={{
                              color: '#4E83BD',
                              backgroundColor: 'rgba(78, 131, 189, 0.1)',
                              '&:hover': {
                                backgroundColor: 'rgba(78, 131, 189, 0.2)',
                              },
                            }}
                          >
                            <Delete sx={{ fontSize: { sm: '18px', md: '20px' } }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handlePageSizeChange}
          rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25, 50]}
          labelRowsPerPage={isMobile ? 'Per page:' : 'Rows per page:'}
          sx={{
            borderTop: '1px solid #ECF0F1',
            '.MuiTablePagination-toolbar': {
              backgroundColor: '#F9FAFB',
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'flex-end' },
              padding: { xs: '8px 4px', sm: '8px 16px' },
              minHeight: { xs: 'auto', sm: '52px' },
            },
            '.MuiTablePagination-spacer': {
              display: { xs: 'none', sm: 'block' },
            },
            '.MuiTablePagination-selectLabel': {
              color: '#2C3E50',
              fontWeight: 500,
              fontSize: { xs: '12px', sm: '14px' },
              margin: { xs: 0, sm: '0 8px 0 0' },
            },
            '.MuiTablePagination-displayedRows': {
              color: '#2C3E50',
              fontWeight: 500,
              fontSize: { xs: '12px', sm: '14px' },
              margin: { xs: '0 4px', sm: '0 16px' },
            },
            '.MuiTablePagination-select': {
              fontSize: { xs: '12px', sm: '14px' },
            },
            '.MuiTablePagination-actions': {
              marginLeft: { xs: 0, sm: '20px' },
              '& .MuiIconButton-root': {
                padding: { xs: '4px', sm: '8px' },
              },
            },
          }}
        />
      </Paper>

      {/* Add/Edit Communication Dialog */}
      <Dialog
        open={showAddForm}
        onClose={() => setShowAddForm(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '16px',
            overflow: 'hidden',
            maxHeight: isMobile ? '100vh' : '90vh',
            margin: { xs: 0, sm: 2 },
          },
        }}
      >
        {/* Dialog Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
            padding: { xs: '12px 16px', sm: '16px 24px' },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: { xs: '16px', sm: '18px' },
            }}
          >
            {editingComm ? 'Edit Communication' : 'Add New Communication'}
          </Typography>
          <IconButton
            onClick={() => setShowAddForm(false)}
            sx={{
              color: 'white',
              padding: { xs: '6px', sm: '8px' },
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              },
            }}
          >
            <CloseIcon sx={{ color: 'white', fill: 'white', fontSize: '22px' }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ padding: 0, overflowX: 'hidden', overflowY: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              minHeight: { xs: 'auto', md: '400px' },
              width: '100%',
              overflowX: 'hidden',
            }}
          >
            {/* Left Column - Basic Information */}
            <Box
              sx={{
                flex: { xs: '1', md: activeTab === 'sms' ? '0 0 100%' : '0 0 40%' },
                padding: { xs: '16px', sm: '24px' },
                backgroundColor: '#FFFFFF',
                borderRight: { xs: 'none', md: activeTab === 'sms' ? 'none' : '1px solid #E5E7EB' },
                borderBottom: { xs: '1px solid #E5E7EB', md: 'none' },
              }}
            >
              {/* Section Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '20px',
                  gap: '10px',
                }}
              >
                <Box
                  sx={{
                    width: '4px',
                    height: '24px',
                    backgroundColor: '#4E83BD',
                    borderRadius: '2px',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#1F2937',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Basic Information
                </Typography>
              </Box>

              {/* Purpose Field */}
              <Box sx={{ marginBottom: '16px' }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6B7280',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Purpose
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={formData.purpose}
                  onChange={(e) => handleFormChange('purpose', e.target.value)}
                  disabled={!!editingComm}
                  SelectProps={{
                    IconComponent: KeyboardArrowDownIcon,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      backgroundColor: '#F8FAFC',
                      '& fieldset': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover fieldset': {
                        borderColor: '#4E83BD',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#4E83BD',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      color: '#1F2937',
                    },
                  }}
                >
                  <MenuItem value="">Select purpose</MenuItem>
                  {Array.isArray(purposes) && purposes.map((p) => (
                    <MenuItem key={p.value || p.id || p} value={p.value || p.purpose || p}>
                      {p.label || p.purpose || (typeof p === 'string' ? p : '')}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Template Name Field */}
              <Box sx={{ marginBottom: '16px' }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6B7280',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Template Name
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter template name"
                  value={formData.template_name}
                  onChange={(e) => handleFormChange('template_name', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      backgroundColor: '#F8FAFC',
                      '& fieldset': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover fieldset': {
                        borderColor: '#4E83BD',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#4E83BD',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      color: '#1F2937',
                    },
                  }}
                />
              </Box>

              {/* Subject Field (Only for Email) */}
              {activeTab === 'email' && (
                <Box sx={{ marginBottom: '16px' }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#6B7280',
                      marginBottom: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Subject
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter subject"
                    value={formData.subject}
                    onChange={(e) => handleFormChange('subject', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: '#F8FAFC',
                        '& fieldset': {
                          borderColor: '#E5E7EB',
                        },
                        '&:hover fieldset': {
                          borderColor: '#4E83BD',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4E83BD',
                        },
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                        color: '#1F2937',
                      },
                    }}
                  />
                </Box>
              )}



              {/* Recipient Type Field */}
              {activeTab === 'email' && (
                <Box sx={{ marginBottom: '16px' }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#6B7280',
                      marginBottom: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Recipient Type
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={formData.recipient_type}
                    onChange={(e) => handleFormChange('recipient_type', e.target.value)}
                    SelectProps={{
                      IconComponent: KeyboardArrowDownIcon,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: '#F8FAFC',
                        '& fieldset': {
                          borderColor: '#E5E7EB',
                        },
                        '&:hover fieldset': {
                          borderColor: '#4E83BD',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4E83BD',
                        },
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                        color: '#1F2937',
                      },
                    }}
                  >
                    <MenuItem value="">Select recipient type</MenuItem>
                    <MenuItem value="User">User</MenuItem>
                    <MenuItem value="Nominee">Nominee</MenuItem>
                  </TextField>
                </Box>
              )}

              {/* Variable Mapping Field - same variable can be added multiple times */}
              {activeTab === 'email' ? (
                <Box sx={{ marginBottom: '16px' }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#6B7280',
                      marginBottom: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Variable Mapping
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={variableMappingAddValue}
                    onChange={(e) => {
                      const key = e.target.value;
                      if (key) {
                        handleAddVariableMapping(key);
                        setVariableMappingAddValue('');
                      }
                    }}
                    SelectProps={{
                      displayEmpty: true,
                      IconComponent: KeyboardArrowDownIcon,
                      renderValue: () => 'Add variable...',
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: '#F8FAFC',
                        '& fieldset': {
                          borderColor: '#E5E7EB',
                        },
                        '&:hover fieldset': {
                          borderColor: '#4E83BD',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4E83BD',
                        },
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                        color: '#1F2937',
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Add variable...</em>
                    </MenuItem>
                    {Array.isArray(variables) && variables.map((v) => (
                      <MenuItem key={v.key} value={v.key}>
                        {v.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, marginTop: 1 }}>
                    {Array.isArray(formData.variable_mapping) && formData.variable_mapping.map((key, index) => {
                      const variable = variables.find(v => v.key === key);
                      return (
                        <Chip
                          key={`${key}-${index}`}
                          label={variable ? variable.label : key}
                          size="small"
                          onDelete={() => handleRemoveVariableMapping(index)}
                          sx={{
                            backgroundColor: '#4E83BD',
                            color: 'white',
                            '& .MuiChip-deleteIcon': {
                              color: 'white',
                              fill: 'white',
                              fontSize: '16px',
                              '&:hover': { color: 'white', opacity: 0.9 },
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                /* Variable Mapping hidden for SMS */
                null
              )}

              {/* Trigger Condition Field (Only for Email) */}
              {activeTab === 'email' && (
                <Box sx={{ marginBottom: '16px' }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#6B7280',
                      marginBottom: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Trigger Condition
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter trigger condition"
                    value={formData.trigger_condition}
                    onChange={(e) => handleFormChange('trigger_condition', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: '#F8FAFC',
                        '& fieldset': {
                          borderColor: '#E5E7EB',
                        },
                        '&:hover fieldset': {
                          borderColor: '#4E83BD',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4E83BD',
                        },
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                        color: '#1F2937',
                      },
                    }}
                  />
                </Box>
              )}

              {/* SMS Specific Fields (Add only) */}
              {activeTab === 'sms' && !editingComm && (
                <>
                  <Box sx={{ marginBottom: '16px' }}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#6B7280',
                        marginBottom: '6px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      DLT Content Template ID
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter DLT Content Template ID"
                      value={formData.dlt_content_template_id}
                      onChange={(e) => handleFormChange('dlt_content_template_id', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          backgroundColor: '#F8FAFC',
                          '& fieldset': {
                            borderColor: '#E5E7EB',
                          },
                          '&:hover fieldset': {
                            borderColor: '#4E83BD',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4E83BD',
                          },
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '14px',
                          color: '#1F2937',
                        },
                      }}
                    />
                  </Box>
                </>
              )}


              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: '12px' }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowAddForm(false)}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#6B7280',
                    borderColor: '#D1D5DB',
                    borderRadius: '10px',
                    padding: '10px 16px',
                    '&:hover': {
                      borderColor: '#9CA3AF',
                      backgroundColor: '#F9FAFB',
                    },
                  }}
                >
                  Cancel
                </Button>
                <ActionButton
                  variant="contained"
                  loading={saving}
                  onClick={() => runSave(handleSave)}
                  loadingText={editingComm ? 'Updating...' : 'Saving...'}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
                    color: 'white',
                    borderRadius: '10px',
                    padding: '10px 16px',
                    boxShadow: '0 4px 12px rgba(78, 131, 189, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #3A6A99 0%, #2D5A80 100%)',
                      boxShadow: '0 6px 16px rgba(78, 131, 189, 0.4)',
                    },
                  }}
                >
                  {editingComm ? 'Update' : 'Save'}
                </ActionButton>
              </Box>
            </Box>

            {/* Right Column - Content (Only for Email) */}
            {activeTab === 'email' && (
              <Box
                sx={{
                  flex: { xs: '1', md: '0 0 60%' },
                  padding: { xs: '16px', sm: '24px' },
                  backgroundColor: '#F8FAFC',
                }}
              >
                {/* Section Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '20px',
                    gap: '10px',
                  }}
                >
                  <Box
                    sx={{
                      width: '4px',
                      height: '24px',
                      backgroundColor: '#10B981',
                      borderRadius: '2px',
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#1F2937',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Message Content
                  </Typography>
                </Box>

                {/* Content Field */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#6B7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Content
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setShowPasteHtmlDialog(true)}
                      sx={{
                        fontSize: '10px',
                        textTransform: 'none',
                        padding: '2px 8px',
                        mr: 1,
                        borderColor: '#4E83BD',
                        color: '#4E83BD',
                        '&:hover': {
                          borderColor: '#3A6A99',
                          backgroundColor: 'rgba(78, 131, 189, 0.04)',
                        }
                      }}
                    >
                      Paste HTML
                    </Button>
                    <Tabs
                      value={editorTab}
                      onChange={(e, newValue) => setEditorTab(newValue)}
                      sx={{
                        minHeight: '32px',
                        '& .MuiTab-root': {
                          minHeight: '32px',
                          padding: '4px 12px',
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'none',
                        },
                      }}
                    >
                      <Tab value="visual" label="Visual" />
                      <Tab value="preview" label="Preview" />
                    </Tabs>
                  </Box>

                  {editorTab === 'visual' && (
                    <Box sx={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      backgroundColor: '#FFFFFF',
                      '& .jodit-container': {
                        border: 'none !important'
                      }
                    }}>
                      <JoditEditor
                        value={formData.email_content}
                        config={{
                          readonly: false,
                          height: isMobile ? 400 : 500,
                          toolbarButtonSize: 'middle',
                          buttons: [
                            'source', '|',
                            'bold', 'italic', 'underline', 'strikethrough', '|',
                            'ul', 'ol', '|',
                            'font', 'fontsize', 'brush', 'paragraph', '|',
                            'image', 'table', 'link', '|',
                            'align', 'undo', 'redo', '|',
                            'hr', 'eraser', 'fullsize'
                          ],
                          uploader: {
                            insertImageAsBase64URI: true
                          },
                          removeButtons: ['about'],
                          showXPathInStatusbar: false,
                          style: {
                            fontFamily: 'sans-serif',
                          },
                          // Ensure editor content matches preview content styling
                          contentStyle: `
                            body {
                              margin: 0;
                              padding: 20px;
                              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                              line-height: 1.5;
                              color: #1F2937;
                            }
                            p { margin-bottom: 1em; }
                          `
                        }}
                        onBlur={(newContent) => handleFormChange('email_content', newContent)}
                      />
                    </Box>
                  )}

                  {editorTab === 'preview' && (
                    <Box
                      sx={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '10px',
                        backgroundColor: '#FFFFFF',
                        width: '100%',
                        minHeight: '400px',
                        overflow: 'hidden',
                      }}
                    >
                      <iframe
                        ref={iframeRef}
                        title="Email Preview"
                        srcDoc={
                          templateWrapper.prefix
                            ? (templateWrapper.prefix + formData.email_content + templateWrapper.suffix)
                            : `<!DOCTYPE html>
                              <html>
                                <head>
                                  <style>
                                    body {
                                      margin: 0;
                                      padding: 20px;
                                      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                                      line-height: 1.5;
                                      color: #1F2937;
                                    }
                                    p { margin-bottom: 1em; }
                                  </style>
                                </head>
                                <body>${formData.email_content}</body>
                              </html>`
                        }
                        onLoad={handleIframeLoad}
                        style={{
                          width: '100%',
                          height: '400px', // Initial height
                          border: 'none',
                          overflow: 'hidden',
                        }}
                        scrolling="no"
                      />
                    </Box>
                  )}

                  <Typography
                    sx={{
                      color: '#4E83BD',
                      fontSize: '11px',
                      mt: 1,
                    }}
                  >
                    Use {'{#var#}'} as a placeholder for each variable in the mapping. Total placeholders must match the number of selected variables.
                  </Typography>
                </Box>

                {/* Placeholder Info Card */}
                <Box
                  sx={{
                    marginTop: '16px',
                    padding: '16px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '10px',
                    border: '1px solid #E5E7EB',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#1F2937',
                      marginBottom: '8px',
                    }}
                  >
                    Available Placeholders
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#6B7280',
                      lineHeight: 1.6,
                    }}
                  >
                    {formData.variable_mapping && formData.variable_mapping.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {formData.variable_mapping.map((key, index) => (
                          <Chip
                            key={`${key}-${index}`}
                            label={`{{${key}}}`}
                            size="small"
                            onClick={() => {
                              const placeholder = `{{${key}}}`;
                              handleFormChange('email_content', formData.email_content + placeholder);
                            }}
                            sx={{
                              backgroundColor: '#F3F4F6',
                              color: '#4B5563',
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: '#E5E7EB',
                              }
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      "Select variables in the mapping to see available placeholders."
                    )}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
          },
        }}
      >
        <DialogContent sx={{ padding: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '18px',
              color: '#2C3E50',
              marginBottom: 2,
            }}
          >
            Confirm Delete
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#6B7280',
              lineHeight: 1.6,
              marginBottom: 3,
            }}
          >
            Are you sure you want to delete this communication? This action cannot be undone.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                color: '#6B7280',
                borderColor: '#D1D5DB',
                paddingX: 3,
                '&:hover': {
                  borderColor: '#9CA3AF',
                  backgroundColor: '#F9FAFB',
                },
              }}
            >
              Cancel
            </Button>
            <ActionButton
              variant="contained"
              loading={deleting}
              onClick={() => runDelete(handleConfirmDelete)}
              loadingText="Deleting..."
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                backgroundColor: '#DC2626',
                color: 'white',
                paddingX: 3,
                fontWeight: 600,
                minWidth: '100px',
                '&:hover': {
                  backgroundColor: '#B91C1C',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#FCA5A5',
                  color: 'white',
                }
              }}
            >
              Delete
            </ActionButton>
          </Box>
        </DialogContent>
      </Dialog>
      {/* Paste HTML Dialog */}
      <Dialog
        open={showPasteHtmlDialog}
        onClose={() => setShowPasteHtmlDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '12px' }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
          color: 'white',
          fontSize: '18px',
          fontWeight: 700,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          Paste HTML Template
          <IconButton onClick={() => setShowPasteHtmlDialog(false)} size="small" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Paste your full HTML email template code below. The system will automatically extract the content and preserve the layout.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={15}
            placeholder="<!DOCTYPE html>..."
            value={pastedHtml}
            onChange={(e) => setPastedHtml(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'monospace',
                fontSize: '13px',
                backgroundColor: '#F9FAFB'
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #E5E7EB' }}>
          <Button onClick={() => setShowPasteHtmlDialog(false)} sx={{ color: '#6B7280' }}>
            Cancel
          </Button>
          <Button
            onClick={handleImportHtml}
            variant="contained"
            disabled={!pastedHtml.trim()}
            sx={{
              backgroundColor: '#4E83BD',
              '&:hover': { backgroundColor: '#3A6A99' }
            }}
          >
            Import Template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Communications;
